import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Store, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNsZWFuJTIwYXJjaGl0ZWN0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODcxOTE4NDZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && !user.must_change_password) navigate("/");
    if (new URLSearchParams(window.location.search).get("expired")) setExpired(true);
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(identifier, password);
      navigate(data.must_change_password ? "/change-password" : "/");
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
      <div className="flex flex-col justify-center px-8 py-12 sm:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
              <Store className="h-5 w-5" />
            </div>
            <span className="font-head text-lg font-600 text-slate-900">SKU Quote</span>
          </div>

          <p className="mb-1 text-xs uppercase tracking-[0.2em] text-slate-400">Internal tool</p>
          <h1 className="font-head text-3xl font-600 tracking-tight text-slate-900">
            Sign in to continue
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Access is gated by your account. Sessions are short-lived and expire after
            inactivity — you will be asked to sign in again.
          </p>

          {expired && (
            <div
              className="mt-5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700"
              data-testid="session-expired"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Your session expired. Please sign in again to continue.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="identifier">Username or email</Label>
              <Input
                id="identifier"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="your Lightspeed username"
                data-testid="login-email"
                className="focus-visible:ring-2 focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                className="focus-visible:ring-2 focus-visible:ring-blue-500"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-blue-600 text-white transition-colors hover:bg-blue-700"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Store data is sourced live from Lightspeed Retail.
          </div>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-head text-2xl font-500 leading-snug tracking-tight">
            Rebuild quotes by SKU. Validate every total. Send with confidence.
          </p>
          <p className="mt-3 max-w-md text-sm text-white/70">
            Totals are checked exactly against the source point-of-sale record before any
            quote can be emailed to a customer.
          </p>
        </div>
      </div>
    </div>
  );
}
